import type { ComponentProps, ReactNode } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import type { DashboardKpiTone } from "@/v2-backoffice-client/uptime/dashboard/uptime.dashboard.types"

export type UptimeDashboardKpiTileProps = ComponentProps<"div"> & {
  // Short uppercase eyebrow, e.g. "Uptime · 24 h".
  label: string
  // Giant headline number, e.g. "60/244" or "74.5%".
  value: string
  // Single-line sub-text below the value. Empty strings render as a
  // non-breaking space so all tiles in the strip stay the same
  // height even when one card has no sub.
  sub: string
  // Tone drives the border / value colour.
  tone: DashboardKpiTone
  // Optional background chart. Renders behind label/value/sub via
  // absolute positioning supplied by the chart component itself --
  // the tile just wires the children through.
  chart?: ReactNode
}

// One tile in the dashboard's top KPI strip. Mirrors the visual
// vocabulary of the regular page's `UptimeOverviewCard` (label /
// value / sub stacked in a card with an optional background
// sparkline) but at the bigger "wall display" scale. The chart slot
// is rendered as a sibling of the text so the text content can sit
// on z-index: 1 and always win against the chart, even at full
// chart contrast.
function UptimeDashboardKpiTile(props: UptimeDashboardKpiTileProps) {
  const { label, value, sub, tone, chart, className, ...divProps } = props
  return (
    <div
      className={cn(
        "uptime-dashboard-kpi",
        `uptime-dashboard-kpi-${tone}`,
        Boolean(chart) && "uptime-dashboard-kpi-with-chart",
        className,
      )}
      {...divProps}
    >
      <div className="uptime-dashboard-kpi-label">{label}</div>
      <div className="uptime-dashboard-kpi-value">{value}</div>
      <div className="uptime-dashboard-kpi-sub">{sub || "\u00a0"}</div>
      {chart}
    </div>
  )
}

export default UptimeDashboardKpiTile
